import React, {useEffect, useMemo, useRef} from 'react';
import {ConfigProvider} from 'antd';
import {Fade} from 'react-awesome-reveal';
import {Helmet} from 'react-helmet';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {rememberCatalogState} from '../../store/slices/CoursesCatalogSlice';
import {getMarketCourses, selectVisibleCourses} from '../../store/slices/MarketCoursesSlice';
import Header from '../components/Header/Header';
import Footer from '../components/Footer/Footer';
import classes from './courses.module.css';
import IntlMessage from '../../components/IntlMessage/IntlMessage';
import BlockLoading from '../../components/LoadingComponents/BlockLoading';
import useLocalizedText from './hooks/useLocalizedText';
import useReducedMotion from './hooks/useReducedMotion';
import useCoursesSearch from './hooks/useCoursesSearch';
import useCourseFilters, {PAGE_SIZE} from './hooks/useCourseFilters';
import SearchField from './components/SearchField';
import FilterBar from './components/FilterBar';
import CourseCard from './components/CourseCard';
import Pagination from './components/Pagination';
import EmptyState from './components/EmptyState';
import CtaStrip from './components/CtaStrip';
import HowItWorks from './components/HowItWorks';
import TrustLogos from './components/TrustLogos';
import CourseFaq from './components/CourseFaq';
import CourseApplyForm from './components/CourseApplyForm';
import {programsLabel} from './courses.utils';
import {SHOW_CALL_REQUEST} from './courses.config';

const CATALOG_FAQ = ['q1', 'q2', 'q3', 'q4', 'q5'];

/** Каталог корпоративных программ: поиск, три фильтра и пагинация по шесть карточек. */
const CoursesListPage = () => {
    const {t} = useTranslation();
    const pick = useLocalizedText();
    const reduced = useReducedMotion();
    const applyRef = useRef(null);

    const scrollToForm = () => {
        applyRef.current?.scrollIntoView({behavior: reduced ? 'auto' : 'smooth', block: 'start'});
    };

    const dispatch = useDispatch();
    const allCourses = useSelector(selectVisibleCourses);
    const {isLoading, isLoaded} = useSelector((state) => state.marketCourses);

    // Список тянем один раз на монтирование: курсов около десятка, а каталогу
    // они нужны все — для поиска по темам модулей и счётчиков в фильтрах
    useEffect(() => {
        if (!isLoaded) dispatch(getMarketCourses());
    }, [dispatch, isLoaded]);

    // Поиск считается до фильтров: его результат — один из входов фильтрации,
    // поэтому строку запроса читаем из URL напрямую, не гоняя хук фильтров дважды
    const [params] = useSearchParams();
    const search = useCoursesSearch(allCourses, params.get('q') || '', pick);

    const filters = useCourseFilters(allCourses, search.active ? search.slugs : null);

    // Запоминаем отбор, чтобы возврат со страницы курса по хлебным крошкам
    // приводил на ту же страницу каталога с теми же фильтрами
    useEffect(() => {
        dispatch(rememberCatalogState(filters.searchString));
    }, [dispatch, filters.searchString]);

    const suggestions = useMemo(() => {
        if (filters.filtered.length > 0) return [];
        const bySlug = new Map(allCourses.map((c) => [c.slug, c]));
        return search.ranked
            .slice(0, 3)
            .map((r) => bySlug.get(r.slug))
            .filter(Boolean);
    }, [filters.filtered.length, search.ranked, allCourses]);

    const faqItems = CATALOG_FAQ.map((key) => ({
        q: t(`coursesPage.faq.${key}.q`),
        a: t(`coursesPage.faq.${key}.a`),
    }));

    // Заголовочная часть анимируется, а поле поиска и факты рендерятся вне <Fade>:
    // обёртка react-awesome-reveal пересоздаётся на каждый рендер и сбивает
    // фокус и ввод в поле, из-за чего поиск выглядел неработающим.
    const heroText = (
        <>
            <span className={classes.heroEyebrow}><IntlMessage id="coursesPage.hero.eyebrow"/></span>
            <h1 className={classes.heroTitle}><IntlMessage id="coursesPage.hero.h1"/></h1>
            <p className={classes.heroSubtitle}><IntlMessage id="coursesPage.hero.subtitle"/></p>
        </>
    );

    const heroBlock = (
        <div className={classes.heroInner}>
            {reduced
                ? heroText
                : <Fade triggerOnce cascade damping={0.12} direction="down">{heroText}</Fade>}
            <SearchField value={filters.query} onChange={filters.setQuery}/>
            <div className={classes.heroFacts}>
                <span>{programsLabel(filters.totalVisible)}</span>
                <span className={classes.factDot}/>
                <span><IntlMessage id="coursesPage.hero.factHours"/></span>
                <span className={classes.factDot}/>
                <span><IntlMessage id="coursesPage.hero.factDoc"/></span>
                <span className={classes.factDot}/>
                <span><IntlMessage id="coursesPage.hero.factPlace"/></span>
            </div>
        </div>
    );

    return (
        <ConfigProvider theme={{token: {colorPrimary: '#163269', borderRadius: 6, fontFamily: "'Montserrat', sans-serif"}}}>
            <div className={classes.page}>
                <Helmet>
                    <title>{t('coursesPage.meta.listTitle')}</title>
                    <meta name="description" content={t('coursesPage.meta.listDescription')}/>
                </Helmet>

                <Header/>

                <section className={classes.hero}>
                    {reduced
                        ? heroBlock
                        : (
                            <Fade triggerOnce cascade damping={0.12} direction="down" style={{width: '100%', display: 'flex', justifyContent: 'center'}}>
                                {heroBlock}
                            </Fade>
                        )}
                </section>

                <FilterBar
                    selected={filters.selected}
                    counts={filters.counts}
                    setGroup={filters.setGroup}
                    reset={filters.reset}
                    hasActiveFilters={filters.hasActiveFilters || !!filters.query}
                    found={filters.filtered.length}
                    total={filters.totalVisible}
                />

                <section className={classes.section}>
                    <div className={classes.container}>
                        {isLoading && !isLoaded ? (
                            <BlockLoading/>
                        ) : filters.filtered.length > 0 ? (
                            <>
                                <div className={classes.grid}>
                                    {filters.pageItems.map((course, i) => (
                                        <CourseCard
                                            key={course.slug}
                                            course={course}
                                            hint={search.hints[course.slug]}
                                            index={i}
                                        />
                                    ))}
                                </div>
                                <Pagination
                                    page={filters.page}
                                    totalPages={filters.totalPages}
                                    onChange={filters.setPage}
                                    total={filters.filtered.length}
                                    pageSize={PAGE_SIZE}
                                />
                            </>
                        ) : (
                            <EmptyState
                                query={filters.query}
                                rescueFilter={filters.rescueFilter}
                                onReset={filters.reset}
                                suggestions={suggestions}
                                onAskProgram={scrollToForm}
                            />
                        )}
                    </div>
                </section>

                <CtaStrip
                    titleId="coursesPage.ctaCard.title"
                    textId="coursesPage.ctaCard.text"
                    buttonId="coursesPage.ctaCard.button"
                    onClick={scrollToForm}
                    secondaryId={SHOW_CALL_REQUEST ? 'coursesPage.ctaCard.call' : undefined}
                    secondaryHref="tel:+77713256171"
                />

                <HowItWorks/>
                <TrustLogos/>
                {/* светлый фон: выше уже идёт серый блок «Как проходит обучение» */}
                <CourseFaq titleId="coursesPage.faq.title" items={faqItems} tone="light"/>

                <CourseApplyForm
                    courses={allCourses}
                    anchorRef={applyRef}
                    tone="alt"
                    prefillComment={filters.query ? t('coursesPage.form.prefillSearch', {query: filters.query}) : ''}
                />

                <Footer/>
            </div>
        </ConfigProvider>
    );
};

export default CoursesListPage;
